import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

const ProductionReportPrint = () => {
  const { productionid: productionIdParam } = useParams();
  const location = useLocation();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // productionid from route OR query
  const productionid =
    Number(productionIdParam || 0) ||
    Number(query.get("prodId")||0) ||
    0;

  // branchid from localstorage OR query
  const branchid = Number(localStorage.getItem("branchId") || 0);

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "0.00";
  };

  const fetchReportData = async () => {
    if (!branchid) {
      setError("branchid missing (localStorage.branchId or query ?branchid=)");
      return;
    }
    if (!productionid) {
      setError("productionid missing in URL. Use /production-report/:productionId");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${BASE_URL}/api/productionListing/printOne`, {
        params: { branchid, productionid },
      });
      console.log("res:", res.data.data);

      if (!res.data?.success) {
        setError(res.data?.message || "Failed to load production report data");
        setReportData(null);
        return;
      }

      setReportData(res.data.data || null);

      // ✅ optional auto print
      setTimeout(() => {
        window.print();
      }, 400);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Server error while loading production report");
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionid, branchid]);

  return (
    <div className="max-w-full mx-auto p-6 bg-gray-200 font-sans text-xs print:p-0 print:max-w-none print:bg-white">
      {/* Outer Gray Border */}
      <div className="border-8 border-gray-500 p-4 bg-white">
        {/* Inner Border */}
        <div className="border-2 border-black">
          {/* Header Section */}
          <div className="border-b-2 border-black">
            <div className="grid grid-cols-[160px_1fr] border-b-2 border-black">
              {/* Logo Section */}
              <div className="border-r-2 border-black p-3 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-28 h-20 bg-black mx-auto flex items-center justify-center mb-1">
                    <div className="text-white text-2xl">📷</div>
                  </div>
                  <div className="text-[8px] font-bold mt-1">NISSI INFOTECH PVT LTD</div>
                </div>
              </div>

              {/* Company Info */}
              <div className="text-center py-3 px-4">
                <h1 className="text-lg font-bold">Nissi Infotech</h1>
                <div className="text-[10px] mt-0.5">Nissi</div>
                <div className="text-sm font-bold mt-0.5">CIVIL CONSTRUCTION</div>
                <div className="text-[10px] mt-1">
                  3/42-12, Nagappa colony, kavundampalayam--641030, Tamil Nadu,
                </div>
                <div className="text-[10px]">
                  Phone No: 9003744644 Email: infraconsoft-implementa
                </div>
              </div>
            </div>

            <div className="text-center py-2 border-b-2 border-black font-bold text-base">
              Production Report
            </div>
          </div>

          {/* Loading/Error */}
          {loading && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
              <div className="mt-2">Loading report data...</div>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded m-4">
              <div className="font-bold">Error loading report:</div>
              <div className="mt-1">{error}</div>
              <div className="mt-2 text-xs text-gray-700">
                Debug: branchid={branchid || "-"} | productionid={productionid || "-"}
              </div>
              <button
                onClick={fetchReportData}
                className="mt-3 bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* Report Details */}
          {!loading && reportData && (
            <>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-x-12 text-xs">
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="font-bold w-36">Date:</span>
                      <span>{reportData.fld_date || "1/27/2021 12:00:00AM"}</span>
                    </div>
                    <div className="flex">
                      <span className="font-bold w-36">Project Name:</span>
                      <span>{reportData.fld_projectname || "road test"}</span>
                    </div>
                    <div className="flex">
                      <span className="font-bold w-36">Production Code:</span>
                      <span>{reportData.fld_productioncode || "2"}</span>
                    </div>
                    <div className="flex">
                      <span className="font-bold w-36">UOM:</span>
                      <span>{reportData.fld_uom || "Nos"}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex">
                      <span className="font-bold w-36">Work Type Name:</span>
                      <span>{reportData.fld_worktypename || "pavement test work"}</span>
                    </div>
                    <div className="flex">
                      <span className="font-bold w-36">Production Name:</span>
                      <span>{reportData.fld_productionname || "testing"}</span>
                    </div>
                    <div className="flex">
                      <span className="font-bold w-36">Production Qty:</span>
                      <span>{reportData.fld_productionqty || "2.00"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="border-t-2 border-b-2 border-black">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b-2 border-black bg-white">
                      <th className="border-r-2 border-black px-3 py-2 text-center w-16 font-bold">
                        S.No
                      </th>
                      <th className="border-r-2 border-black px-3 py-2 text-center font-bold">
                        Item Name
                      </th>
                      <th className="border-r-2 border-black px-3 py-2 text-center w-20 font-bold">
                        UOM
                      </th>
                      <th className="border-r-2 border-black px-3 py-2 text-center w-24 font-bold">
                        BOM Qty
                      </th>
                      <th className="border-r-2 border-black px-3 py-2 text-center w-28 font-bold">
                        Material Qty
                      </th>
                      <th className="border-r-2 border-black px-3 py-2 text-center w-24 font-bold">
                        Price
                      </th>
                      <th className="border-r-2 border-black px-3 py-2 text-center w-28 font-bold">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!reportData.items || reportData.items.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="p-4 text-center text-gray-500">
                          No items available
                        </td>
                      </tr>
                    ) : (
                      <>
                        {reportData.items.map((item, index) => (
                          <tr key={index} className="border-b border-gray-300">
                            <td className="border-r-2 border-black px-3 py-2 text-center">
                              {index + 1}
                            </td>
                            <td className="border-r-2 border-black px-3 py-2 text-left">
                              {item.ItemName || ""}
                            </td>
                            <td className="border-r-2 border-black px-3 py-2 text-center">
                              {item.fld_uom || "Nos"}
                            </td>
                            <td className="border-r-2 border-black px-3 py-2 text-right">
                              {formatNumber(item.Quantity || 0)}
                            </td>
                            <td className="border-r-2 border-black px-3 py-2 text-right">
                              {formatNumber(item.fld_materialqty || 0)}
                            </td>
                            <td className="border-r-2 border-black px-3 py-2 text-right">
                              {formatNumber(item.fld_Price || 0)}
                            </td>
                            <td className="border-r-2 border-black px-3 py-2 text-right">
                              {formatNumber(item.fld_Amount || 0)}
                            </td>
                          </tr>
                        ))}
                        {/* Total Row */}
                        <tr className="border-b-2 border-black font-bold bg-white">
                          <td
                            colSpan="3"
                            className="border-r-2 border-black px-3 py-2 text-right"
                          >
                            Total :
                          </td>
                          <td className="border-r-2 border-black px-3 py-2 text-right">
                            {formatNumber(
                              reportData.items.reduce(
                                (sum, item) => sum + Number(item.Quantity || 0),
                                0
                              )
                            )}
                          </td>
                          <td className="border-r-2 border-black px-3 py-2 text-right">
                            {formatNumber(
                              reportData.items.reduce(
                                (sum, item) => sum + Number(item.fld_materialqty || 0),
                                0
                              )
                            )}
                          </td>
                          <td className="border-r-2 border-black px-3 py-2 text-right"></td>
                          <td className="border-r-2 border-black px-3 py-2 text-right">
                            {formatNumber(
                              reportData.items.reduce(
                                (sum, item) => sum + Number(item.fld_Amount || 0),
                                0
                              )
                            )}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Signature Section */}
              <div className="border-b-2 border-black">
                <div className="grid grid-cols-3 text-xs">
                  <div className="border-r-2 border-black px-4 py-16 text-center">
                    <div className="font-bold">Prepared By</div>
                  </div>
                  <div className="border-r-2 border-black px-4 py-16 text-center">
                    <div className="font-bold">Checked By</div>
                  </div>
                  <div className="px-4 py-16 text-center">
                    <div className="font-bold">Approved By</div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-3 text-[10px] text-gray-700">
                <div>
                  Report generated by ERP-Software - www.infraconsoft.com owned by
                  www.nissiinfotech.com
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:bg-white { background-color: white !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 0.5cm; }
        }
      `}</style>
    </div>
  );
};

export default ProductionReportPrint;